<template>
  <div v-if="paginatorInfo && paginatorInfo.total > 0" class="mbmp-bar" :class="{ 'mbmp-bar--loading': loading }">
    <p class="mbmp-range">
      <span class="mbmp-range-strong">{{ paginatorInfo.firstItem }}</span>
      <span class="mbmp-range-sep">–</span>
      <span class="mbmp-range-strong">{{ paginatorInfo.lastItem }}</span>
      <span class="mbmp-range-of">of</span>
      <span class="mbmp-range-strong">{{ paginatorInfo.total }}</span>
    </p>
    <div class="mbmp-controls">
      <button
        type="button"
        class="mbmp-nav-btn"
        :disabled="loading || !hasPreviousPage"
        aria-label="Previous page"
        @click="previousPage"
      >
        <svg class="mbmp-nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        type="button"
        class="mbmp-edge-btn"
        :disabled="loading || !hasFirstPage"
        @click="goToFirstPage"
      >
        First
      </button>
      <div class="mbmp-pages">
        <button
          v-for="page in pages"
          :key="page"
          type="button"
          class="mbmp-page-btn"
          :class="{ 'mbmp-page-btn-active': paginatorInfo.currentPage === page, 'mbmp-page-btn-loading': loading && paginatorInfo.currentPage === page }"
          :style="paginatorInfo.currentPage === page && !loading ? activePageStyle : null"
          :disabled="loading"
          @click="goToPage(page)"
        >
          <span v-if="loading && paginatorInfo.currentPage === page" class="mbmp-page-spinner" aria-hidden="true"></span>
          <span v-else>{{ page }}</span>
        </button>
      </div>
      <button
        type="button"
        class="mbmp-edge-btn"
        :disabled="loading || !hasLastPage"
        @click="goToLastPage"
      >
        Last
      </button>
      <button
        type="button"
        class="mbmp-nav-btn"
        :disabled="loading || !hasNextPage"
        aria-label="Next page"
        @click="nextPage"
      >
        <svg class="mbmp-nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'master-board-modal-pagination',
  props: {
    paginatorInfo: {
      type: Object,
      default: null,
    },
    accentColor: {
      type: String,
      default: '#2563eb',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    activePageStyle() {
      return {
        backgroundColor: this.accentColor,
        borderColor: this.accentColor,
      };
    },
    hasPreviousPage() {
      return this.paginatorInfo && this.paginatorInfo.currentPage > 1;
    },
    hasNextPage() {
      return this.paginatorInfo && this.paginatorInfo.hasMorePages;
    },
    hasFirstPage() {
      return this.paginatorInfo && this.paginatorInfo.currentPage > 1;
    },
    hasLastPage() {
      if (!this.paginatorInfo) {
        return false;
      }
      return this.paginatorInfo.currentPage < this.paginatorInfo.lastPage;
    },
    totalPages() {
      if (!this.paginatorInfo) {
        return 1;
      }
      return this.paginatorInfo.lastPage || 1;
    },
    pages() {
      if (!this.paginatorInfo) {
        return [];
      }
      var page = this.paginatorInfo.currentPage;
      var totalPages = this.paginatorInfo.lastPage;
      var startPage = Math.max(page - 2, 1);
      var endPage = Math.min(startPage + 4, totalPages);
      var result = [];
      var i;
      for (i = startPage; i <= endPage; i++) {
        result.push(i);
      }
      return result;
    },
  },
  methods: {
    previousPage() {
      if (this.hasPreviousPage && this.paginatorInfo) {
        this.$emit('page-change', this.paginatorInfo.currentPage - 1);
      }
    },
    nextPage() {
      if (this.hasNextPage && this.paginatorInfo) {
        this.$emit('page-change', this.paginatorInfo.currentPage + 1);
      }
    },
    goToPage(page) {
      if (this.loading) {
        return;
      }
      this.$emit('page-change', page);
    },
    goToFirstPage() {
      if (this.hasFirstPage) {
        this.$emit('page-change', 1);
      }
    },
    goToLastPage() {
      if (this.hasLastPage) {
        this.$emit('page-change', this.totalPages);
      }
    },
  },
};
</script>

<style scoped>
.mbmp-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

.mbmp-range {
  margin: 0;
  font-size: 0.75rem;
  color: #64748b;
  white-space: nowrap;
  line-height: 1.2;
}

.mbmp-range-strong {
  font-weight: 700;
  color: #0f172a;
}

.mbmp-range-sep {
  margin: 0 0.125rem;
  color: #94a3b8;
}

.mbmp-range-of {
  margin: 0 0.25rem;
  color: #94a3b8;
}

.mbmp-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.375rem;
}

.mbmp-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mbmp-nav-btn:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.mbmp-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mbmp-nav-icon {
  width: 0.875rem;
  height: 0.875rem;
}

.mbmp-edge-btn {
  height: 1.75rem;
  padding: 0 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mbmp-edge-btn:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1d4ed8;
}

.mbmp-edge-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mbmp-pages {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.mbmp-page-btn {
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0 0.375rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.mbmp-page-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.mbmp-page-btn-active {
  color: #fff;
}

.mbmp-page-btn-active:hover {
  color: #fff;
  opacity: 0.92;
}

.mbmp-bar--loading .mbmp-range {
  opacity: 0.7;
}

.mbmp-page-btn-loading {
  cursor: wait;
}

.mbmp-page-spinner {
  display: block;
  width: 0.875rem;
  height: 0.875rem;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: mbmp-spin 0.6s linear infinite;
}

.mbmp-page-btn-active.mbmp-page-btn-loading {
  background-color: #e2e8f0;
  border-color: #cbd5e1;
}

.mbmp-page-btn-active.mbmp-page-btn-loading .mbmp-page-spinner {
  border-color: rgba(37, 99, 235, 0.2);
  border-top-color: #2563eb;
}

@keyframes mbmp-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
